<template>
  <WorkTooltip v-if="hasTooltip && (!isMobile || showTooltipOnMobile)">
    <template #tooltip-activator="{ on, attrs }">
      <WorkButton
        :class="buttonClass"
        :attrs="attrs"
        v-bind="buttonProps"
        :icon="isIconBtn"
        ripple
        v-on="{ ...on, ...$listeners }"
      >
        <WorkIcon
          v-if="isIconBtn"
          :color="$props.color"
          :size="size"
          :xSmall="xSmall"
          :disabled="disabled"
          :outlined="false"
        >
          {{ icon }}
        </WorkIcon>
        <WorkIconLabel
          v-else
          v-bind="$props"
          :icon="icon"
          :label="label"
          :disabled="disabled"
          :outlined="false"
          :color="$props.color"
        />
      </WorkButton>
    </template>
    <template #tooltip>
      <slot name="tooltip" />
    </template>
  </WorkTooltip>
  <WorkButton
    v-else
    :class="buttonClass"
    v-bind="buttonProps"
    :icon="isIconBtn"
    ripple
    v-on="$listeners"
  >
    <WorkIcon
      v-if="isIconBtn"
      :color="$props.color"
      :size="size"
      :xSmall="xSmall"
      :disabled="disabled"
      :outlined="false"
    >
      {{ icon }}
    </WorkIcon>
    <WorkIconLabel
      v-else
      v-bind="$props"
      :icon="icon"
      :label="label"
      :disabled="disabled"
      :outlined="false"
      :color="$props.color"
    />
  </WorkButton>
</template>

<script>
import WorkButton from '@/components/global_components/buttons/WorkButton';
import WorkIconLabel from '@/components/global_components/icon/WorkIconLabel';
import WorkIcon from '@/components/global_components/icon/WorkIcon';
import WorkTooltip from '@/components/global_components/tooltip/WorkTooltip';
import { filterProps } from '@/utils/functional-component-utils';

const iconTooltipsHiddenOnMobile = ['$wArrowDown'];

/**
 * @component WorkIconButton
 * @description Icon button with optional text label and tooltip.
 * Inherits all props from `WorkButton`. On mobile, certain tooltips are hidden.
 *
 * @prop {String} [size] - Button size.
 * @prop {String} [icon] - Icon name (e.g. `'$wEdit'`).
 * @prop {String} [label] - Optional text next to the icon. Without label only the icon is shown.
 * @prop {Boolean} [disabled=false] - Disable the button.
 * @prop {Boolean} [xSmall=false] - Extra small display.
 * @prop {Boolean} [loading=false] - Show loading state.
 *
 * @slot tooltip - Tooltip content.
 */
export default {
  name: 'WorkIconButton',
  components: {
    WorkButton,
    WorkIconLabel,
    WorkIcon,
    WorkTooltip,
  },
  props: {
    ...WorkButton.props,
    size: { type: String },
    icon: { type: String },
    label: { type: String, required: false },
    disabled: { type: Boolean, default: false },
    xSmall: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  methods: {
    toggleDisabled() {
      this.disabled = !this.disabled;
    },
  },
  computed: {
    isIconBtn() {
      return this.label === undefined;
    },
    isMobile() {
      return this.$vuetify.breakpoint.xs;
    },
    showTooltipOnMobile() {
      return !iconTooltipsHiddenOnMobile.includes(this.icon);
    },
    buttonProps() {
      return this.isIconBtn 
        ? filterProps(this.$props, ['outlined'])
        : this.$props;
    },
    buttonClass() {
      return [
        this.isIconBtn ? 'v-btn--flat' : '',
        'w-button--icon v-btn'
      ];
    },
    hasTooltip() {
      return this.$scopedSlots.tooltip !== undefined;
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep.v-btn {
  padding: 0 !important;
  .w-icon-label-container {
    margin-left: 1rem;
    margin-right: 1rem;
    &.disabled {
      background-color: unset !important;
      border-color: unset !important;
    }
  }
}

::v-deep .w-icon-label {
  margin-left: 0.5rem;
}
</style>
